import { useEffect, useState } from 'react';
import { getTodayKey, msUntilNextMidnight } from '@/lib/tz';

/**
 * Today's YYYY-MM-DD in the user's profile timezone, kept live across the
 * day boundary. A bare getTodayKey() call is only recomputed when something
 * else re-renders, so a tab left open across midnight keeps classifying
 * yesterday as today — e.g. an enabled "today" cell in the Schedule pickers
 * that is actually a past day (YAR-132 AC #9).
 *
 * The hook re-renders its consumer at the timezone's next midnight and
 * re-checks whenever the tab becomes visible again — setTimeout does not
 * fire reliably in backgrounded/suspended tabs. Spurious wake-ups are
 * free: setState bails out when the key is unchanged.
 */
export function useTodayKey(timezone?: string | null): string {
  const [todayKey, setTodayKey] = useState(() => getTodayKey(timezone));

  useEffect(() => {
    // Recompute on timezone change — the state initializer only ran with
    // the mount-time timezone.
    setTodayKey(getTodayKey(timezone));

    let timer: number | undefined;
    const arm = () => {
      // +1s buffer lands the wake-up safely past the boundary.
      timer = window.setTimeout(
        () => {
          setTodayKey(getTodayKey(timezone));
          arm();
        },
        msUntilNextMidnight(timezone) + 1_000,
      );
    };
    arm();

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      setTodayKey(getTodayKey(timezone));
      window.clearTimeout(timer);
      arm();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [timezone]);

  return todayKey;
}
